import React, { useState, useEffect, useRef } from 'react';
import DeliveryDatePicker from './DeliveryDatePicker';
import useDeliveryCalendar from '../hooks/useDeliveryCalendar';
import { useSettings } from '../hooks/useSettings';
import { getTodayInTimezone, getNowHHMMInTimezone } from '../utils/dateUtils';
import { geocodeWithCache } from '../utils/geocodeCache';
import { deliveryApi } from '../utils/api';
import { isClosedOn, isBlackedOut, earliestDeliveryDay, capacityFor, addDaysKey } from '../utils/deliveryDays';
import { isMissingPriorityTime } from '../utils/priorityFee';
import { useAppearance } from '../contexts/AppearanceContext';
import {
  HomeIcon,
  BuildingIcon,
  Flower2Icon,
  HospitalIcon,
  ChurchIcon,
  SchoolIcon,
  HotelIcon,
  StoreIcon,
  TruckIcon,
  AlertTriangleIcon,
} from './icons/LucideIcons';

const DELIVERY_TYPES = [
  { value: 'residential', label: 'Residential', Icon: HomeIcon },
  { value: 'business', label: 'Business', Icon: BuildingIcon },
  { value: 'funeral-home', label: 'Funeral Home', Icon: Flower2Icon },
  { value: 'hospital', label: 'Hospital', Icon: HospitalIcon },
  { value: 'church', label: 'Church', Icon: ChurchIcon },
  { value: 'school', label: 'School', Icon: SchoolIcon },
  { value: 'hotel', label: 'Hotel', Icon: HotelIcon },
];

const TIME_CONDITIONS = [
  { value: 'am', label: 'AM (Morning)' },
  { value: 'pm', label: 'PM (Afternoon)' },
  { value: 'before', label: 'Before (specific time)' },
  { value: 'after', label: 'After (specific time)' },
  { value: 'exact', label: 'Exactly At (specific time)' },
];

// A delivery Time Preference triggers the priority time fee when staff pick
// AM / PM, or a Before / After / Exact time (the latter also need a specific time).
const priorityTimeFeeApplies = (deliveryData) =>
  ['am', 'pm'].includes(deliveryData.timeCondition) ||
  (['before', 'after', 'exact'].includes(deliveryData.timeCondition) && !!deliveryData.priorityTime);

const TIME_SLOTS = [
  { value: '9-11', label: '9:00 AM - 11:00 AM', period: 'morning' },
  { value: '11-1', label: '11:00 AM - 1:00 PM', period: 'midday' },
  { value: '1-3', label: '1:00 PM - 3:00 PM', period: 'afternoon' },
  { value: '3-5', label: '3:00 PM - 5:00 PM', period: 'afternoon' },
  { value: 'asap', label: 'As Soon As Possible', period: 'rush' },
];

const PICKUP_TIME_PREFERENCES = [
  { value: 'AM', label: 'AM (Morning)' },
  { value: 'PM', label: 'PM (Afternoon)' },
  { value: 'before', label: 'Before (specific time)' },
  { value: 'after', label: 'After (specific time)' },
  { value: 'exact', label: 'Exactly At (specific time)' },
];

// Zones, the store point, cutoff, lead times and capacity come from the server
// (GET /api/delivery/rules and /quote, Phase 2). This file used to carry its own
// copy of the Greenville rings and a 2024 holiday list; it no longer does.

const DeliverySection = ({
  deliveryData,
  onDeliveryChange,
  validationErrors = {},
  recipientAddress = {},
  onDeliveryFeeChange,
  onPriorityTimeChange,
  tabIndex = 40,
  isHouseAccount = false,
  onDeliveryQuote,
  itemsLeadTimeDays = 0,
  // 'forest': the New order form's step 2 (the Delivery/Pickup choice sits in
  // the step header, the fee shows under the Recipient step). Edit order keeps
  // the original layout.
  layout,
  // Forest: the quote's progress and zone, for the page's fee strip.
  onFeeStatus,
}) => {
  const { storeHours, blackoutDates, rules: deliveryRules } = useDeliveryCalendar();
  const [deliveryFee, setDeliveryFee] = useState(0);
  const deliveryFeeOverride = deliveryData.deliveryFeeOverride || '';
  const [showOverrideInput, setShowOverrideInput] = useState(false);
  const [priorityTimeFee, setPriorityTimeFee] = useState(0);
  const [isCalculatingFee, setIsCalculatingFee] = useState(false);
  const [deliveryZoneInfo, setDeliveryZoneInfo] = useState(null);
  const { settings } = useSettings();
  const { appearance } = useAppearance();
  const isFable = appearance === 'fable';

  const setTimeCondition = (value) => {
    // Batch updates - clear priorityTime for AM/PM/empty in same update
    const updatedData = {
      ...deliveryData,
      timeCondition: value
    };

    // Clear specific time if switching to AM/PM or clearing selection
    if (value === 'am' || value === 'pm' || !value) {
      updatedData.priorityTime = '';
    }

    onDeliveryChange(updatedData);
  };
  const geocodeDebounceRef = useRef(null);

  // Handle field changes
  // Reset fees to 0 when the order switches to pickup. An effect rather than a
  // step in handleFieldChange, because Fable also offers the Delivery/Pickup
  // choice in the Recipient header (OrderForm), which never calls this
  // component. Fires on the switch only, not when a pickup draft loads.
  const prevFulfillmentRef = useRef(deliveryData.fulfillmentType);
  useEffect(() => {
    const prev = prevFulfillmentRef.current;
    prevFulfillmentRef.current = deliveryData.fulfillmentType;
    if (prev !== 'pickup' && deliveryData.fulfillmentType === 'pickup') {
      setDeliveryFee(0);
      setPriorityTimeFee(0);
      onDeliveryFeeChange?.(0);
      onPriorityTimeChange?.(0);
      setDeliveryZoneInfo(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliveryData.fulfillmentType]);

  // Pickup never carries a delivery fee. A ref, so a quote that was already in
  // flight when staff chose Pickup (or an address typed afterwards) sees the
  // current choice and charges nothing.
  const isPickupRef = useRef(deliveryData.fulfillmentType === 'pickup');
  isPickupRef.current = deliveryData.fulfillmentType === 'pickup';

  const handleFieldChange = (field, value) => {
    const updatedData = {
      ...deliveryData,
      [field]: value,
    };

    // Clear hospital-specific fields if changing away from hospital
    if (field === 'locationType' && value !== 'hospital') {
      delete updatedData.hospitalRecipientType;
      delete updatedData.roomNumber;
    }

    // Clear room number if changing away from patient
    if (field === 'hospitalRecipientType' && value !== 'patient') {
      delete updatedData.roomNumber;
    }

    onDeliveryChange(updatedData);
  };

  // The blackout row for a 'YYYY-MM-DD' date, or null. Rows are strings or
  // { date, reason } (useDeliveryCalendar).
  const blackoutFor = (dateString) => {
    if (!dateString) return null;
    const hit = (blackoutDates || []).find((b) => (typeof b === 'string' ? b : b?.date) === dateString);
    if (!hit) return null;
    return { reason: (typeof hit === 'object' && hit.reason) || null };
  };
  const selectedBlackout = blackoutFor(deliveryData.date);
  // Staff may still book a blacked-out date (a phone exception): "Book anyway"
  // dismisses the notice for that date. Never a hard block on the staff form;
  // the storefront keeps its own.
  const [bookAnywayDate, setBookAnywayDate] = useState(null);

  // A closed day or a blacked-out date, from Settings (store hours, blackout dates).
  const isDateBlocked = (dateString) => {
    if (!dateString) return false;
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return isClosedOn(date, storeHours) || isBlackedOut(date, blackoutDates);
  };

  // Phase 2 date rules: the earliest day this order can go out (cutoff, the
  // shop's default lead time, the slowest item's lead time), the booking
  // window, and per-day capacity. Staff are warned, never blocked.
  const todayKey = getTodayInTimezone();
  const leadDays = Math.max(Number(deliveryRules?.default_lead_time_days) || 0, Number(itemsLeadTimeDays) || 0);
  const earliestKey = earliestDeliveryDay({ today: todayKey, nowHHMM: getNowHHMMInTimezone(), cutoffTime: deliveryRules?.cutoff_time || null, leadDays });
  const latestKey = deliveryRules?.days_ahead ? addDaysKey(todayKey, Number(deliveryRules.days_ahead)) : null;
  const dateCapacity = deliveryData.date ? capacityFor(deliveryData.date, deliveryRules?.capacity) : null;
  const dateHint = (() => {
    if (!deliveryData.date || isHouseAccount) return null;
    if (deliveryData.date < earliestKey && deliveryData.date >= todayKey) {
      return leadDays > 0
        ? `Earliest date for this order is ${earliestKey} (${leadDays}-day lead time). Staff may still book it.`
        : `Past today's ${deliveryRules?.cutoff_time} cutoff — earliest is ${earliestKey}. Staff may still book it.`;
    }
    if (latestKey && deliveryData.date > latestKey) return `Beyond the ${deliveryRules.days_ahead}-day booking window.`;
    if (dateCapacity?.full) return `This day is full (${dateCapacity.used} of ${dateCapacity.limit} booked). Staff may still add to it.`;
    return null;
  })();

  // The shop's same-day cutoff for the tip line ("2 PM", "1:30 PM"); none set,
  // no time is promised (QA B P2-5: the tip said "before 2 PM" for every shop).
  const cutoffLabel = (() => {
    const m = /^(\d{2}):(\d{2})$/.exec(deliveryRules?.cutoff_time || '');
    if (!m) return null;
    const h = Number(m[1]);
    return `${h % 12 || 12}${m[2] === '00' ? '' : `:${m[2]}`} ${h < 12 ? 'AM' : 'PM'}`;
  })();

  // Get minimum date (today in the store timezone - staff can choose same day delivery)
  const getMinDate = () => {
    return getTodayInTimezone();
  };

  // Check whether the chosen date is in the past (compared to today's local date)
  const isPastDate = (dateString) => {
    if (!dateString) return false;
    return dateString < getMinDate();
  };

  // Geocode address to get coordinates (uses session cache to avoid redundant API calls)
  const geocodeAddress = (address) => geocodeWithCache(address);

  // Turn a server quote into the fee the form charges: the zone's fee plus the
  // ASAP surcharge; the priority-time fee is separate and unchanged.
  const lastQuoteRef = useRef(null);
  const applyQuote = (q) => {
    if (isPickupRef.current) {
      setIsCalculatingFee(false);
      return;
    }
    const zone = q && q.zone;
    let baseFee = zone ? Number(zone.fee) || 0 : 0;
    let priorityFee = 0;

    // Time slot surcharge
    if (zone && deliveryData.timeSlot === 'asap') {
      baseFee += 15.0;
    }

    // Priority time surcharge - separate from delivery fee
    // Auto-applied iff:
    //   - global toggle (settings.priorityTimeFeeEnabled) is on, AND
    //   - staff selected an AM / PM / Before / After / Exact time, AND
    //   - location is not a school or funeral home (logistical necessity), AND
    //   - staff has not manually waived via priorityTimeFeeOverride === '0'
    const isExemptLocationType = deliveryData.locationType === 'school' || deliveryData.locationType === 'funeral-home';
    const isManuallyWaived = deliveryData.priorityTimeFeeOverride === '0';
    const triggersPriorityFee = priorityTimeFeeApplies(deliveryData);
    if (settings.priorityTimeFeeEnabled && triggersPriorityFee && !isExemptLocationType && !isManuallyWaived) {
      priorityFee = settings.priorityTimeFeeAmount ?? 10.0;
    }

    setDeliveryFee(baseFee);
    setPriorityTimeFee(priorityFee);
    if (!deliveryData.deliveryFeeOverride) {
      onDeliveryFeeChange?.(baseFee);
    }
    onPriorityTimeChange?.(priorityFee);

    const miles = q && q.distance_miles != null ? Number(q.distance_miles) : null;
    const where = miles != null ? `${miles.toFixed(1)} miles from store` : q && q.zip ? `Zip ${q.zip}` : 'This address';
    setDeliveryZoneInfo({
      distance: miles != null ? miles.toFixed(1) : null,
      zone: zone,
      isExtended: !!(zone && q.out_of_area),
      message: !zone
        ? (q && q.reason) || 'Outside the delivery area'
        : q.out_of_area
          ? `${where} - outside standard delivery area (extended fee: $${baseFee.toFixed(2)})`
          : `${where} - ${zone.name} ($${Number(zone.order_minimum || 0).toFixed(2)} minimum order)`,
    });
    onDeliveryQuote?.(q ? {
      zoneCode: zone ? zone.code : null,
      zoneName: zone ? zone.name : null,
      distanceMiles: miles,
      fee: zone ? Number(zone.fee) : null,
      outOfArea: !!q.out_of_area,
      refused: !!q.refused,
      latitude: q.lat ?? null,
      longitude: q.lng ?? null,
    } : null);
    setIsCalculatingFee(false);
  };

  // Ask the server for the zone and fee. Distance shops need the address
  // geocoded first (client-side Places, as before); zip shops need only the zip.
  const quoteDestination = async ({ lat, lng, zip }) => {
    const response = await deliveryApi.quote(lat != null ? { lat, lng } : { zip });
    const q = { ...(response?.data || {}), lat: lat ?? null, lng: lng ?? null, zip: zip || null };
    lastQuoteRef.current = q;
    applyQuote(q);
  };

  // Effect A: quote when the address changes (debounced 500ms). Zip shops are
  // quoted from the zip alone; distance shops geocode the address first.
  const zipModel = deliveryRules?.model === 'zip';
  useEffect(() => {
    if (!deliveryData.locationType || deliveryData.fulfillmentType === 'pickup') return;
    const zip = String(recipientAddress.zip || '').trim().slice(0, 5);
    if (zipModel) {
      if (!/^\d{5}$/.test(zip)) return;
    } else {
      if (!recipientAddress.address1 || !recipientAddress.city) return;
      if (!window.google || !window.google.maps) return;
    }

    if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);

    setIsCalculatingFee(true);

    geocodeDebounceRef.current = setTimeout(async () => {
      try {
        if (zipModel) {
          await quoteDestination({ zip });
          return;
        }
        const fullAddress = `${recipientAddress.address1}, ${recipientAddress.city}, ${recipientAddress.state || ''} ${recipientAddress.zip}`;
        const deliveryCoords = await geocodeAddress(fullAddress);
        await quoteDestination({ lat: deliveryCoords.lat, lng: deliveryCoords.lng, zip });
      } catch (error) {
        console.warn('Failed to calculate delivery fee:', error);
        if (isPickupRef.current) {
          setIsCalculatingFee(false);
          return;
        }
        const fallbackIsExempt = deliveryData.locationType === 'school' || deliveryData.locationType === 'funeral-home';
        const fallbackIsWaived = deliveryData.priorityTimeFeeOverride === '0';
        const fallbackTriggers = priorityTimeFeeApplies(deliveryData);
        const fallbackPriorityFee =
          settings.priorityTimeFeeEnabled && fallbackTriggers && !fallbackIsExempt && !fallbackIsWaived
            ? settings.priorityTimeFeeAmount ?? 10.0
            : 0;
        // The nearest zone's fee, as the form always fell back to its first ring.
        const fallbackFee = Number(deliveryRules?.zones?.[0]?.fee) || 15.0;
        setDeliveryFee(fallbackFee);
        setPriorityTimeFee(fallbackPriorityFee);
        if (!deliveryData.deliveryFeeOverride) {
          onDeliveryFeeChange?.(fallbackFee);
        }
        onPriorityTimeChange?.(fallbackPriorityFee);
        onDeliveryQuote?.(null);
        setIsCalculatingFee(false);
      }
    }, 500);

    return () => {
      if (geocodeDebounceRef.current) clearTimeout(geocodeDebounceRef.current);
    };
  }, [
    deliveryData.locationType,
    recipientAddress.address1,
    recipientAddress.city,
    recipientAddress.state,
    recipientAddress.zip,
    zipModel,
  ]);

  // Effect B: Recalculate fee without geocoding when time/priority/override
  // or the global priority-fee toggle changes (the toggle loads asynchronously
  // from the API, so we need to recompute after it settles).
  // Before any quote (no address yet, or no fee could be worked out) the
  // priority fee still applies on its own, so the total carries the fee the
  // banner says was added (QA B P2-1).
  useEffect(() => {
    if (lastQuoteRef.current) {
      applyQuote(lastQuoteRef.current);
    } else if (deliveryData.fulfillmentType !== 'pickup') {
      const isExempt = deliveryData.locationType === 'school' || deliveryData.locationType === 'funeral-home';
      const isWaived = deliveryData.priorityTimeFeeOverride === '0';
      const fee = settings.priorityTimeFeeEnabled && priorityTimeFeeApplies(deliveryData) && !isExempt && !isWaived
        ? settings.priorityTimeFeeAmount ?? 10.0
        : 0;
      setPriorityTimeFee(fee);
      onPriorityTimeChange?.(fee);
    }
  }, [
    deliveryData.timeSlot,
    deliveryData.timeCondition,
    deliveryData.priorityTime,
    deliveryData.priorityTimeFeeOverride,
    settings.priorityTimeFeeEnabled,
    settings.priorityTimeFeeAmount,
  ]);

  // Handle delivery fee override changes
  useEffect(() => {
    // An override typed before switching to Pickup stays in the form; it is not charged.
    if (isPickupRef.current) return;
    const override = deliveryData.deliveryFeeOverride || '';
    if (override !== '') {
      const overrideValue = parseFloat(override);
      if (!isNaN(overrideValue) && overrideValue >= 0) {
        onDeliveryFeeChange?.(overrideValue);
      }
    } else {
      // No override - use calculated fee
      onDeliveryFeeChange?.(deliveryFee);
    }
  }, [deliveryData.deliveryFeeOverride, deliveryFee]);

  // Show override input when loading a draft with an active override
  useEffect(() => {
    if (deliveryData.deliveryFeeOverride) {
      setShowOverrideInput(true);
    }
  }, [deliveryData.deliveryFeeOverride]);

  // The quote's progress, zone and calculated fee, for a fee display outside
  // this component (Forest New order: the strip under the Recipient step).
  useEffect(() => {
    onFeeStatus?.({ isCalculating: isCalculatingFee, zoneInfo: deliveryZoneInfo, calculatedFee: deliveryFee });
  }, [onFeeStatus, isCalculatingFee, deliveryZoneInfo, deliveryFee]);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    }).format(value);
  };

  // Get validation error
  const getFieldError = (field) => validationErrors[`delivery.${field}`];

  // Get placeholder text based on delivery type
  const getWindowNotesPlaceholder = () => {
    switch (deliveryData.locationType) {
      case 'business':
      case 'school':
        return 'Latest possible delivery time (e.g., Before 5 PM, After 9 AM...)';
      case 'funeral-home':
      case 'church':
        return 'Event or Service time (e.g., Service at 2 PM, Viewing from 10-12 PM...)';
      case 'hospital':
        return 'Preferred delivery time and any scheduling notes...';
      case 'hotel':
        return 'Check-in time or preferred delivery window (e.g., After 3 PM check-in...)';
      case 'residential':
        return 'e.g., Before 3 PM (meeting), After lunch hour, Avoid morning deliveries...';
      default:
        return 'e.g., Before 3 PM (meeting), After lunch hour, Avoid morning deliveries...';
    }
  };

  // Allow deselecting pickup time preference by clicking the same option again
  const handlePickupTimeToggle = (value) => {
    if (deliveryData.pickupTimePeriod === value) {
      // Deselect if clicking current selection
      handleFieldChange('pickupTimePeriod', '');
    } else {
      handleFieldChange('pickupTimePeriod', value);
    }
  };

  // ── Forest layout (New order, step 2) ──────────────────────────────────────
  // Location types as one row of pill choices, in the design's order.
  const FOREST_LOCATIONS = [
    { value: 'residential', label: 'Residence' },
    { value: 'business', label: 'Business' },
    { value: 'hospital', label: 'Hospital' },
    { value: 'funeral-home', label: 'Funeral home' },
    { value: 'church', label: 'Church' },
    { value: 'school', label: 'School' },
    { value: 'hotel', label: 'Hotel' },
  ];
  const FOREST_TIME_OPTIONS = [
    { value: 'am', pickup: 'AM', label: 'Morning (AM)' },
    { value: 'pm', pickup: 'PM', label: 'Afternoon (PM)' },
    { value: 'before', pickup: 'before', label: 'By a time…' },
    { value: 'after', pickup: 'after', label: 'After a time…' },
    { value: 'exact', pickup: 'exact', label: 'At an exact time…' },
  ];
  const forestInput = 'input-field sm:text-base';
  const pillClass = (selected) =>
    `location-choice relative inline-flex h-ctl cursor-pointer items-center rounded-full border px-3.5 text-[13px] transition-colors duration-[120ms] has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-floral-500 ${
      selected
        ? 'is-selected border-floral-700 bg-floral-700 font-semibold text-white'
        : 'border-gray-300 bg-white font-medium text-gray-900 hover:border-floral-700'
    }`;

  const renderPriorityFeeStatus = () => {
    if (!priorityTimeFeeApplies(deliveryData)) return null;
    const globallyDisabled = !settings.priorityTimeFeeEnabled;
    const isExempt = deliveryData.locationType === 'school' || deliveryData.locationType === 'funeral-home';
    const isWaived = deliveryData.priorityTimeFeeOverride === '0';
    const feeAmount = (settings.priorityTimeFeeAmount ?? 10).toFixed(2);
    const exemptLabel = deliveryData.locationType === 'school' ? 'school' : 'funeral home';

    if (globallyDisabled) {
      return (
        <div className='banner mt-2.5 bg-neutral-bg text-neutral-fg'>
          <span><strong className='font-semibold'>Priority time noted</strong> — auto-fee disabled in store settings</span>
        </div>
      );
    }
    if (isExempt) {
      return (
        <div className='banner banner-green mt-2.5'>
          <span><strong className='font-semibold'>Priority time fee waived</strong> — {exemptLabel} delivery</span>
        </div>
      );
    }
    if (isWaived) {
      return (
        <div className='banner banner-green mt-2.5'>
          <span><strong className='font-semibold'>Priority time fee removed</strong> — manually waived</span>
          <button
            type='button'
            onClick={() => handleFieldChange('priorityTimeFeeOverride', null)}
            className='banner-link'
          >
            Restore ${feeAmount}
          </button>
        </div>
      );
    }
    return (
      <div className='banner banner-warn mt-2.5'>
        <span><strong className='font-semibold'>Priority time fee added</strong> &middot; +${feeAmount} auto-applied</span>
        <button
          type='button'
          onClick={() => handleFieldChange('priorityTimeFeeOverride', '0')}
          className='banner-link'
          aria-label='Remove priority time fee'
        >
          Remove
        </button>
      </div>
    );
  };

  const renderForest = () => {
    const isPickup = deliveryData.fulfillmentType === 'pickup';
    const timeValue = isPickup ? deliveryData.pickupTimePeriod || '' : deliveryData.timeCondition || '';
    const needsSpecificTime = ['before', 'after', 'exact'].includes(timeValue);

    return (
      <div className='flex flex-col gap-[18px]'>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]'>
          {/* Delivery / pickup date */}
          <div className='date-field min-w-0'>
            <span className='field-label input-label'>
              {isPickup ? 'Pickup date' : 'Delivery date'} <span className='input-required'>*</span>
            </span>
            <DeliveryDatePicker
              id='delivery-date'
              label=''
              variant='tiles'
              value={deliveryData.date || ''}
              onChange={(next) => handleFieldChange('date', next)}
              storeHours={storeHours}
              blackoutDates={blackoutDates}
              allowPast={isHouseAccount}
              from={!isHouseAccount && earliestKey > todayKey ? (() => { const [y, m, d] = earliestKey.split('-').map(Number); return new Date(y, m - 1, d); })() : undefined}
              capacity={deliveryRules?.capacity || null}
            />
            {!isPickup && (
              <p className='field-hint input-help'>
                Sunday and holiday delivery may be limited.{cutoffLabel ? ` Same-day rush before ${cutoffLabel}.` : ''}
              </p>
            )}
            {dateHint && <p className='input-help text-warn-fg'>{dateHint}</p>}
            {getFieldError('date') && <p className='input-error-text'>{getFieldError('date')}</p>}
            {selectedBlackout && bookAnywayDate !== deliveryData.date && (
              <p className='input-help flex flex-wrap items-center gap-x-2 text-warn-fg' role='status'>
                <span className='flex items-center'>
                  <AlertTriangleIcon size={14} className='mr-1 shrink-0' />
                  {selectedBlackout.reason
                    ? `This date is blacked out (${selectedBlackout.reason}).`
                    : 'This date is blacked out.'}
                </span>
                <button
                  type='button'
                  onClick={() => setBookAnywayDate(deliveryData.date)}
                  className='font-semibold underline underline-offset-2 hover:no-underline'
                >
                  Book anyway
                </button>
              </p>
            )}
            {deliveryData.date && !selectedBlackout && isDateBlocked(deliveryData.date) && (
              <p className='input-help flex items-center text-warn-fg'>
                <AlertTriangleIcon size={14} className='mr-1 shrink-0' />
                The shop is closed on this date (store hours or a blackout date) —
                delivery may not be available
              </p>
            )}
            {isHouseAccount && isPastDate(deliveryData.date) && (
              <p className='input-help flex items-center text-warn-fg'>
                <AlertTriangleIcon size={14} className='mr-1 shrink-0' />
                Backdated order — this date is in the past. House account will be
                billed for past service.
              </p>
            )}
          </div>

          {/* Time preference */}
          <div className='time-field min-w-0'>
            <label className='field-label input-label' htmlFor='delivery-time-preference'>
              Time preference
            </label>
            <select
              id='delivery-time-preference'
              value={timeValue}
              onChange={(e) => {
                const value = e.target.value;
                if (!isPickup) {
                  setTimeCondition(value);
                  return;
                }
                // Same as the pickup select always did: AM/PM/none clear the time
                const updatedData = { ...deliveryData, pickupTimePeriod: value };
                if (value === 'AM' || value === 'PM' || !value) {
                  updatedData.pickupTime = '';
                }
                onDeliveryChange(updatedData);
              }}
              className={`${forestInput} py-0`}
              tabIndex={tabIndex + 1}
            >
              <option value=''>No preference</option>
              {FOREST_TIME_OPTIONS.map((opt) => (
                <option key={opt.value} value={isPickup ? opt.pickup : opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            {!needsSpecificTime && (
              <p className='field-hint input-help'>Only set a hard time if the customer needs one.</p>
            )}

            {needsSpecificTime && (
              <div className='mt-3'>
                <label className='field-label input-label' htmlFor='delivery-specific-time'>
                  Specific time <span className='input-required'>*</span>
                </label>
                {isPickup ? (
                  <>
                    <input
                      id='delivery-specific-time'
                      type='time'
                      value={deliveryData.pickupTime || ''}
                      onChange={(e) => handleFieldChange('pickupTime', e.target.value)}
                      className={forestInput}
                      tabIndex={tabIndex + 2}
                      required
                    />
                    <p className='field-hint input-help'>
                      {deliveryData.pickupTimePeriod === 'exact'
                        ? 'Customer wants to pick up at exactly this time'
                        : `Customer wants to pick up ${deliveryData.pickupTimePeriod} this time`}
                    </p>
                  </>
                ) : (
                  <>
                    <input
                      id='delivery-specific-time'
                      type='time'
                      name='delivery.priorityTime'
                      value={deliveryData.priorityTime || ''}
                      onChange={(e) => handleFieldChange('priorityTime', e.target.value)}
                      className={`${forestInput}${isMissingPriorityTime(deliveryData) ? ' input-error' : ''}`}
                      tabIndex={tabIndex + 2}
                      required
                    />
                    {isMissingPriorityTime(deliveryData) ? (
                      <p className='input-error-text'>
                        No time yet: fill in the hour, minutes and AM/PM, or the order can't be saved
                      </p>
                    ) : (
                      <p className='field-hint input-help'>
                        Select the {deliveryData.timeCondition === 'exact' ? 'exact' : ''} time for this delivery
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {!isPickup && renderPriorityFeeStatus()}
          </div>
        </div>

        {/* Location type — delivery only */}
        {!isPickup && (
          <div>
            <span className='field-label input-label'>
              Location type <span className='input-required'>*</span>
            </span>
            {/* `relative` on each pill is load-bearing: the radio inside is
                sr-only (position: absolute); without a positioned ancestor it
                escapes the scroll container and stretches the page. */}
            <div className='location-choices flex flex-wrap gap-1.5' role='radiogroup' aria-label='Location type'>
              {FOREST_LOCATIONS.map((type, i) => {
                const selected = deliveryData.locationType === type.value;
                return (
                  <label key={type.value} className={pillClass(selected)}>
                    <input
                      type='radio'
                      name='locationType'
                      value={type.value}
                      checked={selected}
                      onChange={(e) => handleFieldChange('locationType', e.target.value)}
                      className='sr-only'
                      tabIndex={tabIndex + 3 + i}
                    />
                    {type.label}
                  </label>
                );
              })}
            </div>
            {getFieldError('locationType') && (
              <p className='input-error-text'>{getFieldError('locationType')}</p>
            )}
          </div>
        )}

        {/* Location details: hospital recipient type and room, funeral service
            time, hotel reservation */}
        {!isPickup && deliveryData.locationType === 'hospital' && (
          <div className='grid grid-cols-1 gap-x-4 gap-y-3.5 md:grid-cols-2'>
            <div>
              <span className='field-label input-label'>
                Hospital recipient <span className='input-required'>*</span>
              </span>
              <div className='flex flex-wrap gap-1.5' role='radiogroup' aria-label='Hospital recipient'>
                {[{ value: 'patient', label: 'Patient' }, { value: 'employee', label: 'Employee' }].map((opt, i) => {
                  const selected = deliveryData.hospitalRecipientType === opt.value;
                  return (
                    <label key={opt.value} className={pillClass(selected)}>
                      <input
                        type='radio'
                        name='hospitalRecipientType'
                        value={opt.value}
                        checked={selected}
                        onChange={(e) => handleFieldChange('hospitalRecipientType', e.target.value)}
                        className='sr-only'
                        tabIndex={tabIndex + 12 + i}
                      />
                      {opt.label}
                    </label>
                  );
                })}
              </div>
            </div>
            {deliveryData.hospitalRecipientType === 'patient' && (
              <div>
                <label className='field-label input-label' htmlFor='delivery-room-number'>
                  Room number <span className='input-required'>*</span>
                </label>
                <input
                  id='delivery-room-number'
                  type='text'
                  value={deliveryData.roomNumber || ''}
                  onChange={(e) => handleFieldChange('roomNumber', e.target.value)}
                  className={`${forestInput}${getFieldError('roomNumber') ? ' input-error' : ''}`}
                  placeholder='e.g., 205, ICU-12, 3rd Floor Room 8...'
                  tabIndex={tabIndex + 14}
                />
                {getFieldError('roomNumber') && (
                  <p className='input-error-text'>{getFieldError('roomNumber')}</p>
                )}
              </div>
            )}
          </div>
        )}

        {!isPickup && deliveryData.locationType === 'funeral-home' && (
          <div>
            <label className='field-label input-label' htmlFor='delivery-service-time'>
              Service time
            </label>
            <input
              id='delivery-service-time'
              type='text'
              value={deliveryData.serviceTime || ''}
              onChange={(e) => handleFieldChange('serviceTime', e.target.value)}
              className={forestInput}
              placeholder='Flowers delivered before 2pm'
              tabIndex={tabIndex + 15}
            />
            <p className='field-hint input-help'>
              Let us know when the service is scheduled so we can ensure timely delivery
            </p>
          </div>
        )}

        {!isPickup && deliveryData.locationType === 'hotel' && (
          <div>
            <label className='field-label input-label' htmlFor='delivery-hotel-reservation'>
              Reservation name / check-in time
            </label>
            <input
              id='delivery-hotel-reservation'
              type='text'
              value={deliveryData.hotelReservation || ''}
              onChange={(e) => handleFieldChange('hotelReservation', e.target.value)}
              className={forestInput}
              placeholder='The Smiths 4pm'
              tabIndex={tabIndex + 16}
            />
            <p className='field-hint input-help'>
              Help us deliver to the right guest by providing the reservation name and check-in time
            </p>
          </div>
        )}

        {!isPickup && (
          <div>
            <label className='field-label input-label' htmlFor='delivery-window-notes'>
              Delivery window notes <span className='field-hint font-normal text-gray-500'>· optional</span>
            </label>
            <textarea
              id='delivery-window-notes'
              value={deliveryData.windowNotes || ''}
              onChange={(e) => handleFieldChange('windowNotes', e.target.value)}
              rows='2'
              className={`${forestInput} resize-y`}
              placeholder={getWindowNotesPlaceholder()}
              tabIndex={tabIndex + 11}
            />
          </div>
        )}

        {/* Store pickup: the shop's pickup instructions (Settings → Delivery) */}
        {isPickup && (
          <div className='banner banner-green font-normal'>
            <span>
              <strong className='font-semibold'>Store pickup</strong>
              {' · '}
              {deliveryRules?.pickup?.instructions || 'The customer collects the order from the shop.'}
            </span>
          </div>
        )}
      </div>
    );
  };

  if (layout === 'forest') {
    return renderForest();
  }

  return (
    <div className='space-y-6'>
      {/* Section Header */}
      <div className='flex items-center justify-between'>
        {/* <h3 className='text-lg font-semibold text-floral-800'>
          {deliveryData.fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}{' '}
          Information
        </h3> */}
      </div>

      {/* Pickup/Delivery Choice */}
      <div className='space-y-3'>
        <div className='fulfil-choices grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Pickup Option */}
          <button
            type='button'
            onClick={() => handleFieldChange('fulfillmentType', 'pickup')}
            className={`fulfil-choice p-4 border-2 rounded-lg transition-colors text-left ${
              deliveryData.fulfillmentType === 'pickup' ? 'is-selected ' : ''
            }${
              deliveryData.fulfillmentType === 'pickup'
                ? 'border-floral-500 bg-floral-50 text-floral-800'
                : 'border-gray-300 bg-white text-gray-700 hover:border-floral-300'
            }`}
            tabIndex={tabIndex - 1}
          >
            <div className='flex items-center space-x-3'>
              <StoreIcon size={24} className='shrink-0' />
              <div>
                <div className='fulfil-title font-semibold text-lg'>Store Pickup</div>
                <div className='fulfil-desc text-sm opacity-75'>
                  {deliveryRules?.pickup?.instructions || 'Pick up at our store location'}
                </div>
              </div>
            </div>
          </button>

          {/* Delivery Option */}
          <button
            type='button'
            onClick={() => handleFieldChange('fulfillmentType', 'delivery')}
            className={`fulfil-choice p-4 border-2 rounded-lg transition-colors text-left ${
              deliveryData.fulfillmentType === 'delivery' ? 'is-selected ' : ''
            }${
              deliveryData.fulfillmentType === 'delivery'
                ? 'border-floral-500 bg-floral-50 text-floral-800'
                : 'border-gray-300 bg-white text-gray-700 hover:border-floral-300'
            }`}
            tabIndex={tabIndex}
          >
            <div className='flex items-center space-x-3'>
              <TruckIcon size={24} className='shrink-0' />
              <div>
                <div className='fulfil-title font-semibold text-lg'>Local Delivery</div>
                <div className='fulfil-desc text-sm opacity-75'>
                  {deliveryRules?.model === 'zip' ? 'Delivered within our zip code zones' : 'Delivered within our delivery area'}
                </div>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Date and Time Selection */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
        {/* Delivery/Pickup Date */}
        <div className='date-field'>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            {deliveryData.fulfillmentType === 'pickup'
              ? 'Pickup Date (Monday - Saturday)'
              : 'Delivery Date'}{' '}
            *
          </label>
          {/* Quick-pick row over a bare date input: most orders are for today or
              tomorrow, which cost three interactions in a date picker and one
              here. Closed days and blackouts come from SETTINGS now — see
              useDeliveryCalendar for what this used to do instead. */}
          <DeliveryDatePicker
            id='delivery-date'
            label=''
            value={deliveryData.date || ''}
            onChange={(next) => handleFieldChange('date', next)}
            storeHours={storeHours}
            blackoutDates={blackoutDates}
            allowPast={isHouseAccount}
            from={!isHouseAccount && earliestKey > todayKey ? (() => { const [y, m, d] = earliestKey.split('-').map(Number); return new Date(y, m - 1, d); })() : undefined}
            capacity={deliveryRules?.capacity || null}
          />
          {dateHint && (
            <p className='mt-1 text-sm text-amber-700'>{dateHint}</p>
          )}
          {getFieldError('date') && (
            <p className='mt-1 text-sm text-red-600'>{getFieldError('date')}</p>
          )}
          {selectedBlackout && bookAnywayDate !== deliveryData.date && (
            <p className='mt-1 text-sm text-orange-600 flex items-center flex-wrap gap-x-2' role='status'>
              <span className='flex items-center'>
                <AlertTriangleIcon size={16} className='mr-1 shrink-0' />
                {selectedBlackout.reason
                  ? `This date is blacked out (${selectedBlackout.reason}).`
                  : 'This date is blacked out.'}
              </span>
              <button
                type='button'
                onClick={() => setBookAnywayDate(deliveryData.date)}
                className='text-xs text-floral-700 hover:text-floral-800 underline cursor-pointer'
              >
                Book anyway
              </button>
            </p>
          )}
          {deliveryData.date && !selectedBlackout && isDateBlocked(deliveryData.date) && (
            <p className='mt-1 text-sm text-orange-600 flex items-center'>
              <AlertTriangleIcon size={16} className='mr-1 shrink-0' />
              The shop is closed on this date (store hours or a blackout date) —
              delivery may not be available
            </p>
          )}
          {isHouseAccount && isPastDate(deliveryData.date) && (
            <p className='mt-1 text-sm text-amber-700 flex items-center'>
              <svg
                className='w-4 h-4 mr-1'
                fill='none'
                stroke='currentColor'
                viewBox='0 0 24 24'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth='2'
                  d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z'
                />
              </svg>
              Backdated order — this date is in the past. House account will be
              billed for past service.
            </p>
          )}
        </div>

        {/* Time Window - Only for Delivery */}
        {deliveryData.fulfillmentType === 'delivery' && (
          <div className='time-field space-y-3'>
            {/* Time Condition */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                Time Preference
              </label>
              {/* Fable: the design's segmented pills. Same values and the same
                  update as the Rosa select below. */}
              {isFable ? (
                <div className='time-seg flex flex-wrap gap-1.5' role='radiogroup' aria-label='Time preference'>
                  {[{ value: '', label: 'Anytime' }, ...TIME_CONDITIONS.map((c) => ({
                    value: c.value,
                    label: { am: 'AM', pm: 'PM', before: 'Before', after: 'After', exact: 'Exact time' }[c.value] || c.label,
                  }))].map((opt, i) => {
                    const selected = (deliveryData.timeCondition || '') === opt.value;
                    return (
                      <button
                        key={opt.value || 'any'}
                        type='button'
                        role='radio'
                        aria-checked={selected}
                        onClick={() => setTimeCondition(opt.value)}
                        tabIndex={tabIndex + 1}
                        className={`px-3.5 py-1.5 rounded-full border text-[13px] transition-colors ${
                          selected
                            ? 'bg-floral-700 border-floral-700 text-gray-50'
                            : 'border-gray-300 text-gray-900 hover:bg-gray-200'
                        }`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              ) : (
              <select
                value={deliveryData.timeCondition || ''}
                onChange={(e) => setTimeCondition(e.target.value)}
                className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                tabIndex={tabIndex + 1}
              >
                <option value=''>No time preference...</option>
                {TIME_CONDITIONS.map((condition) => (
                  <option key={condition.value} value={condition.value}>
                    {condition.label}
                  </option>
                ))}
              </select>
              )}
            </div>

            {/* Priority Time - Only show for before/after/exact */}
            {deliveryData.timeCondition && ['before', 'after', 'exact'].includes(deliveryData.timeCondition) && (
              <div>
                <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                  Specific Time *
                </label>
                <input
                  type='time'
                  name='delivery.priorityTime'
                  value={deliveryData.priorityTime || ''}
                  onChange={(e) =>
                    handleFieldChange('priorityTime', e.target.value)
                  }
                  className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                  tabIndex={tabIndex + 2}
                  required
                />
                {isMissingPriorityTime(deliveryData) ? (
                  <p className='mt-1 text-xs text-red-600'>
                    No time yet: fill in the hour, minutes and AM/PM, or the order can't be saved
                  </p>
                ) : (
                  <p className='field-hint mt-1 text-xs text-floral-500'>
                    Select the {deliveryData.timeCondition === 'exact' ? 'exact' : ''} time for this delivery
                  </p>
                )}
              </div>
            )}

            {/* Priority Time Fee Status */}
            {priorityTimeFeeApplies(deliveryData) && (() => {
              const globallyDisabled = !settings.priorityTimeFeeEnabled;
              const isExempt = deliveryData.locationType === 'school' || deliveryData.locationType === 'funeral-home';
              const isWaived = deliveryData.priorityTimeFeeOverride === '0';
              const feeAmount = (settings.priorityTimeFeeAmount ?? 10).toFixed(2);
              const exemptLabel = deliveryData.locationType === 'school' ? 'school' : 'funeral home';

              if (globallyDisabled) {
                return (
                  <div className='border rounded-lg p-3 bg-floral-50 border-floral-200'>
                    <div className='flex items-center text-sm text-floral-800'>
                      <svg className='w-4 h-4 mr-2 text-floral-700' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' />
                      </svg>
                      <span>
                        <strong>Priority time noted</strong> — auto-fee disabled in store settings
                      </span>
                    </div>
                  </div>
                );
              }

              if (isExempt) {
                return (
                  <div className='border rounded-lg p-3 bg-floral-50 border-floral-200'>
                    <div className='flex items-center text-sm text-floral-800'>
                      <svg className='w-4 h-4 mr-2 text-floral-700' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' />
                      </svg>
                      <span>
                        <strong>Priority time fee waived</strong> — {exemptLabel} delivery
                      </span>
                    </div>
                  </div>
                );
              }

              if (isWaived) {
                return (
                  <div className='border rounded-lg p-3 bg-floral-50 border-floral-200 flex items-center justify-between'>
                    <div className='flex items-center text-sm text-floral-800'>
                      <svg className='w-4 h-4 mr-2 text-floral-700' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M6 18L18 6M6 6l12 12' />
                      </svg>
                      <span>
                        <strong>Priority time fee removed</strong> — manually waived
                      </span>
                    </div>
                    <button
                      type='button'
                      onClick={() => handleFieldChange('priorityTimeFeeOverride', null)}
                      className='text-xs font-medium text-floral-700 hover:text-floral-900 underline'
                    >
                      Restore ${feeAmount}
                    </button>
                  </div>
                );
              }

              return (
                <div className='border rounded-lg p-3 bg-orange-50 border-orange-200 flex items-center justify-between'>
                  <div className='flex items-center text-sm text-orange-700'>
                    <svg className='w-4 h-4 mr-2 text-orange-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z' />
                    </svg>
                    <span>
                      <strong>Priority time fee added</strong> &middot; +${feeAmount} auto-applied
                    </span>
                  </div>
                  <button
                    type='button'
                    onClick={() => handleFieldChange('priorityTimeFeeOverride', '0')}
                    className='text-xs font-medium text-orange-700 hover:text-orange-900 underline'
                    aria-label='Remove priority time fee'
                  >
                    Remove
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        {/* Pickup Time Preference - Only for Pickup */}
        {deliveryData.fulfillmentType === 'pickup' && (
          <div className='space-y-3'>
            {/* Time Period Dropdown */}
            <div>
              <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                Pickup Time Preference
              </label>
              <select
                value={deliveryData.pickupTimePeriod || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const updatedData = {
                    ...deliveryData,
                    pickupTimePeriod: value
                  };

                  // Clear pickup time if switching to AM/PM or clearing selection
                  if (value === 'AM' || value === 'PM' || !value) {
                    updatedData.pickupTime = '';
                  }

                  onDeliveryChange(updatedData);
                }}
                className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                tabIndex={tabIndex + 1}
              >
                <option value=''>No preference...</option>
                {PICKUP_TIME_PREFERENCES.map((pref) => (
                  <option key={pref.value} value={pref.value}>
                    {pref.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Specific Time Input - Only show when 'before', 'after', or 'exact' is selected */}
            {deliveryData.pickupTimePeriod && ['before', 'after', 'exact'].includes(deliveryData.pickupTimePeriod) && (
              <div>
                <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
                  Specific Time *
                </label>
                <input
                  type='time'
                  value={deliveryData.pickupTime || ''}
                  onChange={(e) =>
                    handleFieldChange('pickupTime', e.target.value)
                  }
                  className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
                  tabIndex={tabIndex + 2}
                  required
                />
                <p className='field-hint mt-1 text-xs text-floral-600'>
                  {deliveryData.pickupTimePeriod === 'exact'
                    ? 'Customer wants to pick up at exactly this time'
                    : `Customer wants to pick up ${deliveryData.pickupTimePeriod} this time`}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delivery Type - Only for Delivery */}
      {deliveryData.fulfillmentType === 'delivery' && (
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-3'>
            Delivery Location Type *
          </label>
          {/* `relative` on these labels is load-bearing, not decoration: the
              radios inside are `sr-only`, which is `position: absolute`. Without
              a positioned ancestor their containing block is the page itself, so
              they escape the app's scroll container and stretch the document to
              their static position — giving the whole app a phantom scrollbar
              that runs ~2000px past the end of the content. */}
          <div className='location-choices grid grid-cols-2 md:grid-cols-3 gap-3'>
            {DELIVERY_TYPES.map((type) => (
              <label
                key={type.value}
                className={`location-choice relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  deliveryData.locationType === type.value
                    ? 'is-selected border-floral-500 bg-floral-500 shadow-md text-white'
                    : 'border-floral-200 hover:border-floral-300 bg-white hover:bg-gray-50 text-floral-800'
                }`}
              >
                <input
                  type='radio'
                  name='locationType'
                  value={type.value}
                  checked={deliveryData.locationType === type.value}
                  onChange={(e) =>
                    handleFieldChange('locationType', e.target.value)
                  }
                  className='sr-only'
                  tabIndex={tabIndex + 3 + DELIVERY_TYPES.indexOf(type)}
                />
                <type.Icon size={18} className='mr-2 shrink-0' />
                <span className='text-sm font-medium'>
                  {type.label}
                </span>
              </label>
            ))}
          </div>
          {getFieldError('locationType') && (
            <p className='mt-1 text-sm text-red-600'>
              {getFieldError('locationType')}
            </p>
          )}
        </div>
      )}

      {/* Hospital Delivery Options - Only for Hospital */}
      {deliveryData.fulfillmentType === 'delivery' &&
        deliveryData.locationType === 'hospital' && (
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-3'>
              Hospital Delivery Recipient Type *
            </label>
            <div className='grid grid-cols-2 gap-4'>
              <label
                className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  deliveryData.hospitalRecipientType === 'patient'
                    ? 'border-floral-500 bg-white'
                    : 'border-floral-200 hover:border-floral-300 bg-white hover:bg-white'
                }`}
              >
                <input
                  type='radio'
                  name='hospitalRecipientType'
                  value='patient'
                  checked={deliveryData.hospitalRecipientType === 'patient'}
                  onChange={(e) =>
                    handleFieldChange('hospitalRecipientType', e.target.value)
                  }
                  className='sr-only'
                  tabIndex={tabIndex + 12}
                />
                <span className='text-lg mr-2'>🛌</span>
                <span className='text-sm font-medium text-floral-800'>
                  Patient
                </span>
              </label>

              <label
                className={`relative flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                  deliveryData.hospitalRecipientType === 'employee'
                    ? 'border-floral-500 bg-white'
                    : 'border-floral-200 hover:border-floral-300 bg-white hover:bg-white'
                }`}
              >
                <input
                  type='radio'
                  name='hospitalRecipientType'
                  value='employee'
                  checked={deliveryData.hospitalRecipientType === 'employee'}
                  onChange={(e) =>
                    handleFieldChange('hospitalRecipientType', e.target.value)
                  }
                  className='sr-only'
                  tabIndex={tabIndex + 13}
                />
                <span className='text-lg mr-2'>👩‍⚕️</span>
                <span className='text-sm font-medium text-floral-800'>
                  Employee
                </span>
              </label>
            </div>
          </div>
        )}

      {/* Room Number - Only for Hospital Patient */}
      {deliveryData.fulfillmentType === 'delivery' &&
        deliveryData.locationType === 'hospital' &&
        deliveryData.hospitalRecipientType === 'patient' && (
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Room Number *
            </label>
            <input
              type='text'
              value={deliveryData.roomNumber || ''}
              onChange={(e) => handleFieldChange('roomNumber', e.target.value)}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='e.g., 205, ICU-12, 3rd Floor Room 8...'
              tabIndex={tabIndex + 14}
            />
            {getFieldError('roomNumber') && (
              <p className='mt-1 text-sm text-red-600'>
                {getFieldError('roomNumber')}
              </p>
            )}
          </div>
        )}

      {/* Service Time - Only for Funeral Home */}
      {deliveryData.fulfillmentType === 'delivery' &&
        deliveryData.locationType === 'funeral-home' && (
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Service Time
            </label>
            <input
              type='text'
              value={deliveryData.serviceTime || ''}
              onChange={(e) => handleFieldChange('serviceTime', e.target.value)}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='Flowers delivered before 2pm'
              tabIndex={tabIndex + 15}
            />
            <p className='field-hint mt-1 text-xs text-floral-600'>
              🕊️ Let us know when the service is scheduled so we can ensure timely delivery
            </p>
          </div>
        )}

      {/* Hotel Reservation - Only for Hotel */}
      {deliveryData.fulfillmentType === 'delivery' &&
        deliveryData.locationType === 'hotel' && (
          <div>
            <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
              Reservation Name / Check-in time
            </label>
            <input
              type='text'
              value={deliveryData.hotelReservation || ''}
              onChange={(e) => handleFieldChange('hotelReservation', e.target.value)}
              className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
              placeholder='The Smiths 4pm'
              tabIndex={tabIndex + 16}
            />
            <p className='field-hint mt-1 text-xs text-floral-600'>
              🏨 Help us deliver to the right guest by providing the reservation name and check-in time
            </p>
          </div>
        )}

      {/* Special Location Instructions - Only for Delivery */}
      {deliveryData.fulfillmentType === 'delivery' && (
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            Special Location Instructions
          </label>
          <textarea
            value={deliveryData.locationInstructions || ''}
            onChange={(e) =>
              handleFieldChange('locationInstructions', e.target.value)
            }
            rows='3'
            className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
            placeholder='e.g., Ring doorbell, leave with receptionist, specific floor/room number...'
            tabIndex={tabIndex + 10}
          />
        </div>
      )}

      {/* Preferred Delivery Window Notes - Only for Delivery */}
      {deliveryData.fulfillmentType === 'delivery' && (
        <div>
          <label className='field-label block text-sm font-medium text-floral-700 mb-2'>
            Preferred Delivery Window Notes
          </label>
          <textarea
            value={deliveryData.windowNotes || ''}
            onChange={(e) => handleFieldChange('windowNotes', e.target.value)}
            rows='2'
            className='w-full px-3 py-2 border border-floral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-floral-500 focus:border-floral-500'
            placeholder={getWindowNotesPlaceholder()}
            tabIndex={tabIndex + 11}
          />
        </div>
      )}

      {/* Fulfillment Summary */}
      {(deliveryData.date || deliveryData.fulfillmentType) && (
        <div className='bg-floral-50 border border-floral-200 rounded-lg p-4'>
          <h4 className='text-sm font-medium text-floral-800 mb-2'>
            {deliveryData.fulfillmentType === 'pickup' ? 'Pickup' : 'Delivery'}{' '}
            Summary
          </h4>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div className='space-y-1 text-sm text-floral-700'>
            {deliveryData.date && (
              <p>
                <span className='font-medium'>Date:</span>{' '}
                {(() => {
                  // Parse date as local date to avoid timezone issues
                  const [year, month, day] = deliveryData.date.split('-').map(Number);
                  const dateObj = new Date(year, month - 1, day);
                  return dateObj.toLocaleDateString('en-US', {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric',
                  });
                })()}
                {isDateBlocked(deliveryData.date) && (
                  <span className='ml-2 text-orange-600 text-xs'>
                    (⚠️ Closed)
                  </span>
                )}
              </p>
            )}
            {deliveryData.fulfillmentType === 'delivery' &&
              deliveryData.timeCondition &&
              deliveryData.priorityTime && (
                <p>
                  <span className='font-medium'>Time:</span>{' '}
                  <span className='text-red-600 font-semibold'>
                    {
                      TIME_CONDITIONS.find(
                        (c) => c.value === deliveryData.timeCondition
                      )?.label
                    }{' '}
                    {new Date(
                      `2000-01-01T${deliveryData.priorityTime}`
                    ).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit',
                      hour12: true,
                    })}
                  </span>
                </p>
              )}
            {deliveryData.fulfillmentType === 'delivery' &&
              deliveryData.timeSlot && (
                <p>
                  <span className='font-medium'>Time:</span>{' '}
                  {
                    TIME_SLOTS.find(
                      (slot) => slot.value === deliveryData.timeSlot
                    )?.label
                  }
                </p>
              )}
            {deliveryData.fulfillmentType === 'pickup' &&
              deliveryData.pickupTimePeriod && (
                <p>
                  <span className='font-medium'>Time Preference:</span>{' '}
                  {['before', 'after', 'exact'].includes(deliveryData.pickupTimePeriod) && deliveryData.pickupTime ? (
                    <span className='text-floral-700 font-semibold'>
                      {deliveryData.pickupTimePeriod === 'before' ? 'Before' : deliveryData.pickupTimePeriod === 'after' ? 'After' : 'Exactly At'}{' '}
                      {new Date(`2000-01-01T${deliveryData.pickupTime}`).toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true,
                      })}
                    </span>
                  ) : (
                    PICKUP_TIME_PREFERENCES.find(
                      (pref) => pref.value === deliveryData.pickupTimePeriod
                    )?.label
                  )}
                </p>
              )}
            {deliveryData.fulfillmentType === 'delivery' &&
              deliveryData.locationType && (
                <p>
                  <span className='font-medium'>Location Type:</span>{' '}
                  {
                    DELIVERY_TYPES.find(
                      (type) => type.value === deliveryData.locationType
                    )?.label
                  }
                </p>
              )}
            {deliveryData.fulfillmentType === 'delivery' &&
              deliveryData.locationType === 'hospital' &&
              deliveryData.hospitalRecipientType && (
                <p>
                  <span className='font-medium'>Recipient Type:</span>{' '}
                  {deliveryData.hospitalRecipientType === 'patient'
                    ? '🛌 Patient'
                    : '👩‍⚕️ Employee'}
                </p>
              )}
            {deliveryData.fulfillmentType === 'delivery' &&
              deliveryData.locationType === 'hospital' &&
              deliveryData.hospitalRecipientType === 'patient' &&
              deliveryData.roomNumber && (
                <p>
                  <span className='font-medium'>Room Number:</span>{' '}
                  {deliveryData.roomNumber}
                </p>
              )}
            </div>
            
            {/* Extended Delivery Warning Banner */}
            {deliveryZoneInfo?.isExtended && !isCalculatingFee && deliveryData.fulfillmentType === 'delivery' && (
              <div className='flex items-center space-x-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-md'>
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5 text-amber-500 flex-shrink-0">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
                </svg>
                <span className='text-xs text-amber-700'>
                  This address is outside the standard delivery area. An out-of-area delivery fee has been calculated. Use Manual Override to adjust if needed.
                </span>
              </div>
            )}

            {/* Delivery Fee Section */}
            {deliveryData.fulfillmentType === 'delivery' && (deliveryFee > 0 || priorityTimeFee > 0 || deliveryZoneInfo) && (
              <div className='space-y-2'>
                <div className={`text-sm font-medium ${deliveryZoneInfo?.isExtended ? 'text-amber-600' : 'text-floral-600'}`}>
                  {priorityTimeFee > 0 ? 'Delivery + Priority Fee' : 'Delivery Fee'}
                </div>
                <div className={`text-lg font-bold ${deliveryZoneInfo?.isExtended ? 'text-amber-800' : 'text-floral-800'}`}>
                  {isCalculatingFee ? (
                    <span className='flex items-center'>
                      <div className='animate-spin rounded-full h-4 w-4 border-b-2 border-floral-600 mr-2'></div>
                      Calculating...
                    </span>
                  ) : deliveryZoneInfo && !deliveryZoneInfo.zone ? (
                    <span className='text-red-600'>Outside Delivery Area</span>
                  ) : (
                    <>
                      {formatCurrency((deliveryFeeOverride !== '' ? parseFloat(deliveryFeeOverride) : deliveryFee) + priorityTimeFee)}
                      {deliveryZoneInfo?.isExtended && (
                        <span className='ml-2 text-xs font-normal text-amber-600'>(Extended)</span>
                      )}
                    </>
                  )}
                </div>
                {priorityTimeFee > 0 && !isCalculatingFee && (
                  <div className='text-xs text-orange-600'>
                    Includes ${priorityTimeFee.toFixed(2)} priority time fee
                  </div>
                )}
                {deliveryZoneInfo && !isCalculatingFee && (
                  <div
                    className={`text-xs mt-1 ${
                      deliveryZoneInfo.isExtended
                        ? 'text-amber-600'
                        : deliveryZoneInfo.zone
                        ? 'text-floral-500'
                        : 'text-red-500'
                    }`}
                  >
                    {deliveryZoneInfo.message}
                  </div>
                )}
                {/* Manual Override - Clickable text that expands to input */}
                {!isCalculatingFee && deliveryFee > 0 && (
                  <div className='mt-2 pt-2 border-t border-gray-200'>
                    {!showOverrideInput ? (
                      <button
                        type='button'
                        onClick={() => setShowOverrideInput(true)}
                        className='text-xs text-floral-700 hover:text-floral-800 underline cursor-pointer'
                      >
                        Manual Override
                      </button>
                    ) : (
                      <div>
                        <div className='flex items-center justify-between mb-1'>
                          <label className='text-xs text-gray-600'>
                            Manual Override
                          </label>
                          <button
                            type='button'
                            onClick={() => {
                              setShowOverrideInput(false);
                              handleFieldChange('deliveryFeeOverride', '');
                            }}
                            className='text-xs text-gray-500 hover:text-gray-700'
                          >
                            Cancel
                          </button>
                        </div>
                        <input
                          type='number'
                          step='0.01'
                          min='0'
                          value={deliveryFeeOverride}
                          onChange={(e) => handleFieldChange('deliveryFeeOverride', e.target.value)}
                          placeholder={`Calculated: $${deliveryFee.toFixed(2)}`}
                          className='w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-floral-500 focus:border-transparent text-sm'
                          autoFocus
                        />
                        {deliveryFeeOverride !== '' && (
                          <div className='field-hint text-xs text-floral-700 mt-1'>
                            Overriding calculated fee of ${deliveryFee.toFixed(2)}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <div className='flex justify-between items-center pt-4 border-t border-floral-100'>
        <div className='field-hint text-sm text-floral-600'>
          💡 Tip: {cutoffLabel ? `Rush deliveries available same-day before ${cutoffLabel}. ` : ''}Sundays and
          holidays may have limited availability.
        </div>

        <div className='field-hint flex items-center space-x-2 text-xs text-floral-500'>
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='2'
              d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
          <span>Delivery fees calculated automatically</span>
        </div>
      </div>
    </div>
  );
};

export default DeliverySection;
